
import React, { useState, useEffect } from 'react';
import { Bell } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { NotificationList } from './NotificationList';
import { NotificationSummary } from './NotificationSummary';

export const NotificationBell = () => {
  const [unreadCount, setUnreadCount] = useState(0);
  const [isOpen, setIsOpen] = useState(false);

  // Fetch notifications and unread count
  useEffect(() => {
    const fetchNotifications = async () => {
      try {
        // Simulate API call to fetch notifications
        const storedNotifications = JSON.parse(localStorage.getItem('notifications') || '[]');
        const unread = storedNotifications.filter((n: any) => !n.read).length;
        setUnreadCount(unread);
      } catch (error) {
        console.error('Error fetching notifications:', error);
      }
    };

    fetchNotifications();

    // Set up interval to periodically check for new notifications
    const interval = setInterval(fetchNotifications, 60000); // Check every minute
    return () => clearInterval(interval);
  }, []);

  // Handle opening the notifications panel
  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
    
    // Mark notifications as read when opening the panel
    if (open && unreadCount > 0) {
      try {
        const storedNotifications = JSON.parse(localStorage.getItem('notifications') || '[]');
        const updatedNotifications = storedNotifications.map((n: any) => ({ ...n, read: true }));
        localStorage.setItem('notifications', JSON.stringify(updatedNotifications));
        setUnreadCount(0);
      } catch (error) {
        console.error('Error updating notifications:', error);
      }
    }
  };

  // Create a demo notification (for testing)
  const createDemoNotification = () => {
    try {
      const storedNotifications = JSON.parse(localStorage.getItem('notifications') || '[]');
      const newNotification = {
        id: `notification-${Date.now()}`,
        title: 'New Notification',
        message: 'This is a demo notification.',
        time: new Date().toISOString(),
        read: false,
        type: 'info'
      };
      
      localStorage.setItem('notifications', JSON.stringify([newNotification, ...storedNotifications]));
      setUnreadCount(prev => prev + 1);
    } catch (error) {
      console.error('Error creating demo notification:', error);
    }
  };

  return (
    <Popover open={isOpen} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button 
          variant="ghost" 
          size="icon" 
          className="relative h-8 w-8 rounded-full"
          aria-label={`Notifications ${unreadCount > 0 ? `(${unreadCount} unread)` : ''}`}
          title={`Notifications ${unreadCount > 0 ? `(${unreadCount} unread)` : ''}`}
          onDoubleClick={createDemoNotification} // Double-click to create demo notification
        >
          <Bell className="h-4 w-4" />
          {unreadCount > 0 && (
            <span className="absolute top-1 right-1 h-2 w-2 rounded-full bg-red-500" />
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-0" align="end">
        <div className="p-4 pb-0">
          <NotificationSummary />
        </div>
        <NotificationList />
      </PopoverContent>
    </Popover>
  );
};
